import * as React from 'react';
import {useEffect, useState, useTransition} from 'react';
import {fetchQuery, graphql, useLazyLoadQuery, useMutation, useRelayEnvironment} from 'react-relay';
import {CheckCircle2, QrCode, Tablet, Printer} from 'lucide-react';
import {OpsDevicesPageQuery} from './__generated__/OpsDevicesPageQuery.graphql';
import {OpsDevicesPageStartMutation, OpsDevicesPageStartMutation$data} from './__generated__/OpsDevicesPageStartMutation.graphql';
import {OpsDevicesPageRevokeMutation} from './__generated__/OpsDevicesPageRevokeMutation.graphql';
import {OpsDevicesPagePairingQuery} from './__generated__/OpsDevicesPagePairingQuery.graphql';
import {formatDateTime, ghostButton, inputClass, primaryButton} from './lib';

const QUERY = graphql`
    query OpsDevicesPageQuery {
        devices { id name kind actsAs lastSeenAt revokedAt createdAt }
    }`;

const START = graphql`
    mutation OpsDevicesPageStartMutation($input: StartDevicePairingInput!) {
        startDevicePairing(input: $input) {
            pairing { id name expiresAt }
            pairingUrl
            qrSvg
            userErrors { message }
        }
    }`;

const PAIRING = graphql`
    query OpsDevicesPagePairingQuery($id: ID!) {
        devicePairing(id: $id) { id claimedAt device { name } }
    }`;

const REVOKE = graphql`
    mutation OpsDevicesPageRevokeMutation($input: RevokeDeviceInput!) {
        revokeDevice(input: $input) {
            device { id revokedAt }
            userErrors { message }
        }
    }`;

type Started = NonNullable<OpsDevicesPageStartMutation$data['startDevicePairing']>;

// The QR an iPad scans from its Station settings. Polls until the code is claimed,
// then reports the new device; the code is dead after one use or ten minutes.
const PairingPanel: React.FC<{ started: Started; onDone: () => void }> = ({started, onDone}) => {
    const environment = useRelayEnvironment();
    const [claimedName, setClaimedName] = useState<string | null>(null);
    const [now, setNow] = useState(Date.now());
    const expiresAt = new Date(started.pairing!.expiresAt).getTime();
    const remaining = Math.max(0, Math.round((expiresAt - now) / 1000));

    useEffect(() => {
        if (claimedName || remaining === 0) return;
        const tick = setInterval(() => setNow(Date.now()), 1000);
        const poll = setInterval(async () => {
            const res = await fetchQuery<OpsDevicesPagePairingQuery>(environment, PAIRING, {id: started.pairing!.id}, {fetchPolicy: 'network-only'}).toPromise();
            if (res?.devicePairing?.claimedAt) setClaimedName(res.devicePairing.device?.name ?? started.pairing!.name);
        }, 2000);
        return () => { clearInterval(tick); clearInterval(poll); };
    }, [claimedName, remaining === 0]);

    if (claimedName) {
        return (
            <div className="border border-emerald-500/40 bg-emerald-500/5 p-6 mb-6 flex items-center gap-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-400"/>
                <div className="flex-1">
                    <p className="text-white font-semibold">{claimedName} is paired.</p>
                    <p className="text-gray-400 text-sm">It's signed in as a station and listed below.</p>
                </div>
                <button type="button" onClick={onDone} className={primaryButton}>Done</button>
            </div>
        );
    }

    const svg = `data:image/svg+xml;utf8,${encodeURIComponent(started.qrSvg ?? '')}`;
    return (
        <div className="border border-gray-800 p-6 mb-6 flex flex-col md:flex-row gap-8 items-center">
            <div className={`bg-white p-4 ${remaining === 0 ? 'opacity-20' : ''}`}>
                <img src={svg} alt="Pairing QR code" className="w-64 h-64" style={{imageRendering: 'pixelated'}}/>
            </div>
            <div className="flex-1 space-y-3">
                <h2 className="text-xl font-display font-bold">Pair “{started.pairing!.name}”</h2>
                <ol className="text-gray-300 text-sm list-decimal list-inside space-y-1">
                    <li>Open <span className="text-white">Love Wins Ops</span> on the iPad.</li>
                    <li>In Station settings, tap <span className="text-white">Scan pairing code</span>.</li>
                    <li>Point the camera at this code.</li>
                </ol>
                <p className="text-xs text-gray-500">
                    {remaining > 0
                        ? `Waiting for the iPad… code expires in ${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}. It works once.`
                        : 'This code has expired. Start a new pairing.'}
                </p>
                <details className="text-xs text-gray-500">
                    <summary className="cursor-pointer hover:text-gray-300">No camera? Copy the pairing link</summary>
                    <input readOnly value={started.pairingUrl ?? ''} onFocus={(e) => e.target.select()} className={`${inputClass} mt-2 font-mono`}/>
                    <p className="mt-1">Paste it into Station settings on the iPad.</p>
                </details>
                <button type="button" onClick={onDone} className={ghostButton}>{remaining > 0 ? 'Cancel' : 'Close'}</button>
            </div>
        </div>
    );
};

const NewPairing: React.FC<{ onStarted: (s: Started) => void; onCancel: () => void }> = ({onStarted, onCancel}) => {
    const [commit, inFlight] = useMutation<OpsDevicesPageStartMutation>(START);
    const [name, setName] = useState('Front counter iPad');
    const [error, setError] = useState<string | null>(null);
    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        commit({
            variables: {input: {name: name.trim()}},
            onCompleted: (res) => {
                const payload = res.startDevicePairing;
                if (payload?.pairing) onStarted(payload);
                else setError(payload?.userErrors.map((u) => u.message).join('; ') ?? 'Something went wrong');
            },
            onError: (err) => setError(err.message),
        });
    };
    return (
        <form onSubmit={submit} className="border border-gray-800 p-4 mb-6 flex flex-wrap items-end gap-3">
            <label className="text-[11px] text-gray-500 flex-1 min-w-[16rem]">Name this iPad
                <input value={name} onChange={(e) => setName(e.target.value)} className={inputClass} autoFocus/>
            </label>
            <button type="submit" disabled={inFlight || !name.trim()} className={primaryButton}>Show pairing code</button>
            <button type="button" onClick={onCancel} className={ghostButton}>Cancel</button>
            {error && <p className="w-full text-red-400 text-xs">{error}</p>}
        </form>
    );
};

// Station devices: the counter iPad and the print agent Pi. Pair new iPads by QR;
// revoke anything lost or retired.
const OpsDevicesPage: React.FC = () => {
    const [fetchKey, setFetchKey] = useState(0);
    const [, startTransition] = useTransition();
    const data = useLazyLoadQuery<OpsDevicesPageQuery>(QUERY, {}, {fetchPolicy: 'network-only', fetchKey});
    const [revoke] = useMutation<OpsDevicesPageRevokeMutation>(REVOKE);
    const [naming, setNaming] = useState(false);
    const [started, setStarted] = useState<Started | null>(null);
    const refresh = () => startTransition(() => setFetchKey((k) => k + 1));

    const revokeDevice = (id: string, name: string) => {
        if (!window.confirm(`Revoke ${name}? It stops working immediately and has to be paired again.`)) return;
        revoke({variables: {input: {deviceId: id}}});
    };

    return (
        <div>
            <div className="flex items-end justify-between mb-6">
                <h1 className="text-3xl font-display font-bold">Devices</h1>
                {!naming && !started && (
                    <button type="button" onClick={() => setNaming(true)} className={`${primaryButton} flex items-center gap-2`}>
                        <QrCode className="w-4 h-4"/> Pair an iPad
                    </button>
                )}
            </div>

            {naming && <NewPairing onStarted={(s) => { setNaming(false); setStarted(s); }} onCancel={() => setNaming(false)}/>}
            {started && <PairingPanel started={started} onDone={() => { setStarted(null); refresh(); }}/>}

            <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-wider text-gray-500 text-left">
                    <tr><th className="py-2">Device</th><th>Acts as</th><th>Last seen</th><th>Added</th><th/></tr>
                </thead>
                <tbody>
                    {data.devices.length === 0 && (
                        <tr><td colSpan={5} className="py-6 text-gray-500">No station devices yet.</td></tr>
                    )}
                    {data.devices.map((d) => {
                        const Icon = d.kind === 'pi' ? Printer : Tablet;
                        return (
                            <tr key={d.id} className={`border-t border-gray-900 ${d.revokedAt ? 'text-gray-600' : 'text-gray-300'}`}>
                                <td className="py-3">
                                    <span className="flex items-center gap-2">
                                        <Icon className="w-4 h-4"/>
                                        <span className={d.revokedAt ? 'line-through' : 'text-white'}>{d.name}</span>
                                        <span className="text-[11px] text-gray-500 uppercase">{d.kind === 'pi' ? 'print agent' : 'iPad'}</span>
                                    </span>
                                </td>
                                <td className="text-xs">{d.actsAs}</td>
                                <td className="text-xs">{d.lastSeenAt ? formatDateTime(d.lastSeenAt) : 'never'}</td>
                                <td className="text-xs">{formatDateTime(d.createdAt)}</td>
                                <td className="text-right">
                                    {d.revokedAt
                                        ? <span className="text-xs">revoked {formatDateTime(d.revokedAt)}</span>
                                        : <button type="button" onClick={() => revokeDevice(d.id, d.name)} className="text-xs text-red-400 hover:text-red-300">Revoke</button>}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default OpsDevicesPage;
